// The card the reader is working on (board pane, card page, focus mode): loads the full card,
// owns its controller (keys, one press with Undo or arm → confirm, edits) and tells the Dock which
// card it is.
import { useEffect } from "react";
import type { CardSummary, FeedSummary } from "@shared/api";
import { useAccountLookup } from "@web/lib/accounts";
import { useCard } from "@web/lib/feedQueries";
import { useVerdictCommand } from "@web/measure/verdictStore";
import { cardTarget } from "@web/measure/verdicts";
import { PhoneCard } from "@web/notify/PhoneCard";
import { dock } from "@web/shell/dockStore";
import type { CardOutcome } from "./cardOps";
import { DecisionCard } from "./card/DecisionCard";
import { useDraftAhead } from "./card/draftAhead";
import { useCardController } from "./card/useCardController";

export interface ActiveCardProps {
  summary: CardSummary;
  feed: FeedSummary | null;
  active: boolean;
  onToggleThread: () => void;
  /** Bind `o` inside the card (card page, focus) rather than on the host screen. */
  bindThreadKey?: boolean;
  onEscape?: () => boolean;
  canEscape?: boolean;
  onOutcome?: (cardId: string, outcome: CardOutcome) => void;
  /** Where a `!` verdict on this card was given. */
  verdictSurface?: "card" | "focus";
  /** On a phone, swipe left to archive and right to snooze (focus mode). */
  swipe?: boolean;
}

export function ActiveCard({
  summary,
  feed,
  active,
  onToggleThread,
  bindThreadKey = false,
  onEscape,
  canEscape,
  onOutcome,
  verdictSurface = "card",
  swipe = false,
}: ActiveCardProps) {
  const card = useCard(summary.id);
  const { list, colorIndex, label } = useAccountLookup();
  const controller = useCardController(card.data, {
    active,
    onOutcome,
    onToggleThread: bindThreadKey ? onToggleThread : undefined,
    onEscape,
    canEscape,
  });

  // U4: a selected card that needs a reply gets its draft written ahead (once; off under the cap).
  useDraftAhead(summary, active);

  // `!`: tell MeeMail what it got wrong (U0). The full card knows whether a draft exists.
  useVerdictCommand(() => cardTarget(card.data ?? summary, verdictSurface), active);

  const title = card.data?.title ?? summary.title;
  useEffect(() => {
    if (!active) return;
    dock.setCard({ id: summary.id, title });
    return () => dock.setCard(null);
  }, [active, summary.id, title]);

  // On a phone: the next step as a large bottom button, and swipes where asked (U10).
  return (
    <PhoneCard card={card.data} controller={controller} active={active} swipe={swipe}>
      <DecisionCard
        summary={summary}
        card={card.data}
        controller={controller}
        defaultCleanupKind={feed?.defaultCleanupKind ?? null}
        showAccount={list.length > 1}
        colorIndex={summary.accountId ? colorIndex(summary.accountId) : 0}
        accountLabel={summary.accountEmail ? label(summary.accountEmail) : undefined}
        active={active}
        onOpenThread={onToggleThread}
        loadError={card.isError && !card.data}
        onRetry={() => void card.refetch()}
      />
    </PhoneCard>
  );
}
